"use client";

import Image from "next/image";
import  { useState } from "react";

import { useLang } from "@/hooks/use-lang";
import readBook from "@/public/robo/read-book.png";

import { BarChart, PieChart } from "./chart";
import { BarData } from "./chart/BarChart";
import { PieData } from "./chart/PieChart";
import { Slider } from "./slider";

type Level = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

const Program = () => {
  const { t } = useLang();

  const PROGRAMS = {
    1: {
      lv: "1",
      name: t.roboAdvisor.program.lv[0],
      reward: "4.49",
      management: "0.25",
      portfolio: ["100", "0"],
      assets: ["0.31", "69.09", "30.59", "0"],
      description: t.roboAdvisor.program.lv[1],
    },
    2: {
      lv: "2",
      name: t.roboAdvisor.program.lv[0],
      reward: "5.50",
      management: "0.40",
      portfolio: ["100", "0"],
      assets: ["0.35", "69.27", "30.39", "0"],
      description: t.roboAdvisor.program.lv[1],
    },
    3: {
      lv: "3",
      name: t.roboAdvisor.program.lv[2],
      reward: "6.92",
      management: "0.50",
      portfolio: ["100", "0"],
      assets: ["0.39", "67.12", "30.25", "2.24"],
      description: t.roboAdvisor.program.lv[3],
    },
    4: {
      lv: "4",
      name: t.roboAdvisor.program.lv[2],
      reward: "7.72",
      management: "0.50",
      portfolio: ["100", "0"],
      assets: ["0.42", "61.70", "30.42", "7.47"],
      description: t.roboAdvisor.program.lv[3],
    },
    5: {
      lv: "5",
      name: t.roboAdvisor.program.lv[2],
      reward: "8.47",
      management: "0.50",
      portfolio: ["100", "0"],
      assets: ["0.46", "63.16", "30.39", "5.99"],
      description: t.roboAdvisor.program.lv[3],
    },
    6: {
      lv: "6",
      name: t.roboAdvisor.program.lv[4],
      reward: "13.69",
      management: "0.60",
      portfolio: ["50", "50"],
      assets: ["0.29", "34.55", "65.16", "0"],
      description: t.roboAdvisor.program.lv[5],
    },
    7: {
      lv: "7",
      name: t.roboAdvisor.program.lv[4],
      reward: "16.33",
      management: "0.65",
      portfolio: ["40", "60"],
      assets: ["0.33", "24.68", "72.00", "2.99"],
      description: t.roboAdvisor.program.lv[5],
    },
    8: {
      lv: "8",
      name: t.roboAdvisor.program.lv[4],
      reward: "17.86",
      management: "0.75",
      portfolio: ["30", "70"],
      assets: ["0.32", "18.51", "78.93", "2.24"],
      description: t.roboAdvisor.program.lv[5],
    },
    9: {
      lv: "9",
      name: t.roboAdvisor.program.lv[6],
      reward: "19.39",
      management: "0.80",
      portfolio: ["20", "80"],
      assets: ["0.30", "12.34", "85.87", "1.49"],
      description: t.roboAdvisor.program.lv[7],
    },
    10: {
      lv: "10",
      name: t.roboAdvisor.program.lv[6],
      reward: "19.61",
      management: "0.85",
      portfolio: ["20", "80"],
      assets: ["0.31", "12.32", "85.87", "1.50"],
      description: t.roboAdvisor.program.lv[5],
    },
  };

  const [level, setLevel] = useState<[Level, Level]>([6, 5]);
  const data = PROGRAMS[level[0]];
  const lastData = PROGRAMS[level[1]];

  const pieData: PieData[] = [
    {
      title: t.roboAdvisor.program.text[6],
      color: "#fbc02a",
      value: data.portfolio[0],
      lastValue: lastData.portfolio[0],
    },
    {
      title: t.roboAdvisor.program.text[7],
      color: "#e35046",
      value: data.portfolio[1],
      lastValue: lastData.portfolio[1],
    },
  ];

  const barData: BarData[] = [
    {
      title: t.roboAdvisor.program.text[9],
      color: "#5361b6",
      value: data.assets[0],
    },
    {
      title: t.roboAdvisor.program.text[10],
      color: "#3b97e8",
      value: data.assets[1],
    },
    {
      title: t.roboAdvisor.program.text[11],
      color: "#734fbb",
      value: data.assets[2],
    },
    {
      title: t.roboAdvisor.program.text[12],
      color: "#00acc1",
      value: data.assets[3],
    },
  ];

  const onSliderChange = (arr: [Level, Level]) => {
    setLevel(arr);
  };

  return (
    <div className="flex h-[1740px] flex-col xl:h-[1314px]">
      <div className="flex grow justify-center bg-[#b6ebd5] px-5 pb-10 @container md:px-10">
        <div className="flex max-w-[1216px] flex-col bg-white px-5 pb-8 pt-[55px] md:px-10 xl:gap-y-16">
          {/* title */}
          <div className="text-center">
            <h2 className="text-2xl font-bold sm:text-3xl md:text-3xl">
              {t.roboAdvisor.program.text[0]}
            </h2>
          </div>
          {/* contents */}
          <div className="flex flex-col gap-x-[100px] gap-y-16 pb-0 xl:flex-row">
            {/* slider section */}
            <div className="w-full">
              <p
                className="flex items-center py-5 md:py-1"
                aria-label={`risk level ${level[0]}`}
              >
                <span className="mr-2 whitespace-nowrap font-medium sm:text-xl md:text-2xl">
                  {t.roboAdvisor.program.text[1]}
                </span>
                <span className="text-2xl font-medium text-[#2B9356] sm:text-3xl md:text-3xl">
                  {level[0]}
                </span>
              </p>
              <h3 className="mb-8 text-2xl font-bold text-[#2B9356] sm:text-3xl md:text-3xl">
                {data.name}
              </h3>
              <Slider
                defaultValue={level[0]}
                onChange={(lv: number) => {
                  onSliderChange([lv as Level, level[0]]);
                }}
              />
              <div className="mt-10 max-w-[500px] -translate-x-5 bg-[#00ac6a] py-12 text-center text-lg font-semibold text-white sm:text-3xl md:-translate-x-10">
                {`${t.roboAdvisor.program.text[2]} ${data.reward}%`}
                <br />
                {`${t.roboAdvisor.program.text[3]} ${data.management}${t.roboAdvisor.program.text[4]}`}
              </div>
            </div>
            {/* charts section */}
            <div className="flex w-full flex-col">
              <p className="text-sm font-semibold md:text-lg">
                {t.roboAdvisor.program.text[5]}
              </p>
              <div className="my-6 rounded-md p-6 shadow-xl">
                <PieChart data={pieData} />
              </div>
              <p className="text-sm font-semibold md:text-lg">
                {t.roboAdvisor.program.text[6]}
              </p>
              <div className="my-6 rounded-md p-6 shadow-xl">
                <BarChart data={barData} />
              </div>
              <p className="text-sm xl:text-lg">{data.description}</p>
            </div>
          </div>
        </div>
      </div>
      {/* background img */}
      <div className="relative h-[210px] bg-[url('/robo/bg-grass2.png')] bg-[length:640px_109px] sm:bg-[length:1280px_218px]">
        <Image
          priority
          alt="read-book"
          src={readBook}
          className="absolute bottom-0 left-0 h-fit max-h-[500px] w-full object-contain object-left-bottom"
        />
      </div>
    </div>
  );
};

export default Program;
